import { UseMutationOptions, UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';
import queryString from 'query-string';

import api from '.';
import { GetAssignmentsRequestDto } from '../dtos/requests/get-assignments.request.dto';
import { SubmitAssignmentRequestDto } from '../dtos/requests/submit-assignment.request.dto';
import { BaseResponseDto } from '../dtos/responses/base.response.dto';
import { EmptyResponseDto } from '../dtos/responses/empty.response.dto';
import { GetAssignmentDescriptionResponseDto } from '../dtos/responses/get-assignment-description.response.dto';
import { GetAssignmentProblemsResponseDto } from '../dtos/responses/get-assignment-problems.response.dto';
import { GetAssignmentStatisticsResponseDto } from '../dtos/responses/get-assignment-statistics.response.dto';
import { GetAssignmentSubmitResponseDto } from '../dtos/responses/get-assignment-submit.response.dto';
import { GetAssignmentsResponseDto } from '../dtos/responses/get-assignments.response.dto';

const getAssignments = (query: GetAssignmentsRequestDto) => {
  return api<BaseResponseDto<GetAssignmentsResponseDto[]>, Error>(
    `/student/assignment?${queryString.stringify(query)}`,
    {
      method: 'GET',
    },
  );
};

export const useGetAssignments = (
  query: GetAssignmentsRequestDto,
  options?: Omit<UseQueryOptions<BaseResponseDto<GetAssignmentsResponseDto[]>, Error>, 'queryFn'>,
) => {
  return useQuery({
    queryKey: ['student', 'assignment', query],
    queryFn: () => getAssignments(query),
    ...options,
  });
};

const getAssignment = (assignmentId: string) => {
  return api<BaseResponseDto<GetAssignmentProblemsResponseDto>, Error>(
    `/student/assignment/${assignmentId}`,
    {
      method: 'GET',
    },
  );
};

export const useGetAssignment = (
  assignmentId: string,
  options?: Omit<
    UseQueryOptions<BaseResponseDto<GetAssignmentProblemsResponseDto>, Error>,
    'queryFn'
  >,
) => {
  return useQuery({
    queryKey: ['student', 'assignment', assignmentId],
    queryFn: () => getAssignment(assignmentId),
    ...options,
  });
};

const getAssignmentStatistics = (assignmentId: string) => {
  return api<BaseResponseDto<GetAssignmentStatisticsResponseDto>, Error>(
    `/student/assignment/${assignmentId}/statistics`,
    {
      method: 'GET',
    },
  );
};

export const useGetAssignmentStatistics = (
  assignmentId: string,
  options?: Omit<
    UseQueryOptions<BaseResponseDto<GetAssignmentStatisticsResponseDto>, Error>,
    'queryFn'
  >,
) => {
  return useQuery({
    queryKey: ['student', 'assignment', assignmentId, 'statistics'],
    queryFn: () => getAssignmentStatistics(assignmentId),
    ...options,
  });
};

const getAssignmentDescription = (assignmentId: string) => {
  return api<BaseResponseDto<GetAssignmentDescriptionResponseDto>, Error>(
    `/student/assignment/${assignmentId}/description`,
    {
      method: 'GET',
    },
  );
};

export const useGetAssignmentDescription = (
  assignmentId: string,
  options?: Omit<
    UseQueryOptions<BaseResponseDto<GetAssignmentDescriptionResponseDto>, Error>,
    'queryFn'
  >,
) => {
  return useQuery({
    queryKey: ['student', 'assignment', assignmentId, 'description'],
    queryFn: () => getAssignmentDescription(assignmentId),
    ...options,
  });
};

const submitAssignment = (
  data: SubmitAssignmentRequestDto & { assignmentId: string },
): Promise<BaseResponseDto<EmptyResponseDto>> => {
  const { assignmentId, ...rest } = data;
  return api<BaseResponseDto<EmptyResponseDto>, SubmitAssignmentRequestDto>(
    `/student/assignment/${assignmentId}/submit`,
    {
      method: 'POST',
      data: rest,
      timeout: 30000,
    },
  );
};

export const useSubmitAssignment = (
  options?: Partial<
    Omit<
      UseMutationOptions<
        BaseResponseDto<EmptyResponseDto>,
        unknown,
        SubmitAssignmentRequestDto & { assignmentId: string }
      >,
      'mutationFn'
    >
  >,
) => {
  return useMutation<
    BaseResponseDto<EmptyResponseDto>,
    unknown,
    SubmitAssignmentRequestDto & { assignmentId: string }
  >({
    mutationFn: submitAssignment,
    ...options, // 전달받은 옵션을 병합
  });
};
const tempSubmitAssignment = (
  data: SubmitAssignmentRequestDto & { assignmentId: string },
): Promise<BaseResponseDto<EmptyResponseDto>> => {
  const { assignmentId, ...rest } = data;
  return api<BaseResponseDto<EmptyResponseDto>, SubmitAssignmentRequestDto>(
    `/student/assignment/${assignmentId}/temp-submit`,
    {
      method: 'POST',
      data: rest,
    },
  );
};

export const useTempSubmitAssignment = (
  options?: Partial<
    Omit<
      UseMutationOptions<
        BaseResponseDto<EmptyResponseDto>,
        unknown,
        SubmitAssignmentRequestDto & { assignmentId: string }
      >,
      'mutationFn'
    >
  >,
) => {
  return useMutation<
    BaseResponseDto<EmptyResponseDto>,
    unknown,
    SubmitAssignmentRequestDto & { assignmentId: string }
  >({
    mutationFn: tempSubmitAssignment,
    ...options, // 전달받은 옵션을 병합
  });
};
const getSubmitAssignment = (assignmentId: string) => {
  return api<BaseResponseDto<GetAssignmentSubmitResponseDto>, Error>(
    `/student/assignment/${assignmentId}/submit`,
    {
      method: 'GET',
    },
  );
};

export const useGetSubmitAssignment = (
  assignmentId: string,
  options?: Omit<
    UseQueryOptions<BaseResponseDto<GetAssignmentSubmitResponseDto>, Error>,
    'queryFn'
  >,
) => {
  return useQuery({
    queryKey: ['student', 'assignment', assignmentId, 'submit'],
    queryFn: () => getSubmitAssignment(assignmentId),
    ...options,
  });
};
